import React from 'react';

export interface AQIDisplayProps {
  pm25?: number;
  co2?: number;
  tvoc?: number;
  showLabels?: boolean;
  compact?: boolean;
  className?: string;
}

/**
 * AQI Display Style02 - Gauge Layout
 * 
 * 圓形儀表風格的空氣品質顯示
 */
export const Style02_Gauge: React.FC<AQIDisplayProps> = ({
  pm25,
  co2,
  tvoc,
  showLabels = true,
  className = ''
}) => {
  // Calculate percentage for gauge (0-100)
  const getPM25Percentage = (value: number): number => {
    // Max scale: 500 µg/m³
    return Math.min(100, (value / 500) * 100);
  };

  const getCO2Percentage = (value: number): number => {
    // Max scale: 5000 ppm
    return Math.min(100, (value / 5000) * 100);
  };

  const getTVOCPercentage = (value: number): number => {
    // Max scale: 10000 µg/m³
    return Math.min(100, (value / 10000) * 100);
  };

  // Color based on percentage
  const getColor = (percentage: number): string => {
    if (percentage <= 20) return '#4ade80'; // green-400
    if (percentage <= 40) return '#a3e635'; // lime-400
    if (percentage <= 60) return '#facc15'; // yellow-400
    if (percentage <= 80) return '#fb923c'; // orange-400
    return '#f87171'; // red-400
  };

  const hasPM25 = pm25 !== undefined;
  const hasCO2 = co2 !== undefined;
  const hasTVOC = tvoc !== undefined;
  const itemCount = [hasPM25, hasCO2, hasTVOC].filter(Boolean).length;

  if (itemCount === 0) {
    return null;
  }

  // Single gauge component
  const Gauge: React.FC<{
    value: number;
    max: number;
    unit: string;
    label: string;
    percentage: number;
  }> = ({ value, unit, label, percentage }) => {
    const color = getColor(percentage);
    const circumference = 2 * Math.PI * 40; // radius = 40
    const strokeDashoffset = circumference - (percentage / 100) * circumference;

    return (
      <div className="flex flex-col items-center">
        <div className="relative w-24 h-24">
          {/* Background circle */}
          <svg className="w-full h-full -rotate-90" viewBox="0 0 100 100">
            <circle
              cx="50"
              cy="50"
              r="40"
              fill="none"
              stroke="#1e293b"
              strokeWidth="8"
            />
            {/* Progress circle */}
            <circle
              cx="50"
              cy="50"
              r="40"
              fill="none"
              stroke={color}
              strokeWidth="8"
              strokeLinecap="round"
              strokeDasharray={circumference}
              strokeDashoffset={strokeDashoffset}
              style={{ transition: 'stroke-dashoffset 0.5s ease' }}
            />
          </svg>
          {/* Center text */}
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className="text-xl font-bold text-white">{value}</span>
            <span className="text-xs text-slate-400">{unit}</span>
          </div>
        </div>
        {showLabels && (
          <span className="text-xs text-slate-400 mt-2">{label}</span>
        )}
      </div>
    );
  };

  return (
    <div className={`flex justify-center gap-6 ${className}`}>
      {hasPM25 && (
        <Gauge
          value={pm25!}
          max={500}
          unit="µg/m³"
          label="PM2.5"
          percentage={getPM25Percentage(pm25!)}
        />
      )}
      {hasCO2 && (
        <Gauge
          value={co2!}
          max={5000}
          unit="ppm"
          label="CO₂"
          percentage={getCO2Percentage(co2!)}
        />
      )}
      {hasTVOC && (
        <Gauge
          value={tvoc!}
          max={10000}
          unit="µg/m³"
          label="TVOC"
          percentage={getTVOCPercentage(tvoc!)}
        />
      )}
    </div>
  );
};

export default Style02_Gauge;
